/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './PortfolioGrowthChart.scss';

import { findIndex, max } from 'lodash';
import * as d3 from 'd3';
import numeral from 'numeral';
import { array, bool } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { mmmddyyyy, utcFormat } from 'services/Dates';

import { svgWidth } from 'components/shared/ChartHelper/ChartHelper';

import {
  registerRenderOnWindowResize,
  unregisterRenderOnWindowResize
} from 'components/shared/ComponentHelper/ComponentHelper';

import {
  growthTickFormat,
  growthTickValues,
  roundUp
} from 'services/Calculation';

const currencyFormat = '$0,0.00';

// Width calculated from layout, never fixed
const SVG_FULL_HEIGHT = 379;
const SVG_TOOLTIP_MID_X = 45;
const SVG_ENDPOINT_RADIUS = 4;
const Y_TICK_PADDING = 12;
const TOOLTIP_VERTICAL_PADDING = 8;
const TOOLTIP_HORIZONTAL_PADDING = 16;
const HOVER_LEGEND_VERTICAL_OFFSET = -100;

// Generate minimum possible width for container to still fit all labels
export const getMinPossibleWidth = (nodes, padding) =>
  max(
    nodes.map((node) => {
      const bounds = node.getBBox();
      return bounds.x + bounds.width + padding;
    })
  );

/*
 * This is used to calculate the distance
 * the y coordinate of a line given the x-coordinate of the cursor,
 * the calculated point is then used to translate
 * the Market Value dot on hover. This keeps the dot on
 * the line at all times and prevents step-wise jumps
 * */
const getPointOnLine = (x, line) => {
  const path = line.node();
  const totalLength = path.getTotalLength();

  let beginning = x;
  let end = totalLength;
  let target;
  let pos;

  for (;;) {
    target = Math.ceil((beginning + end) / 2);
    pos = path.getPointAtLength(target);
    if ((target === end || target === beginning) && pos.x !== x) {
      break;
    }
    if (pos.x > x) {
      end = target;
    } else if (pos.x < x) {
      beginning = target;
    } else {
      break;
    } //position found
  }

  return pos;
};

class PortfolioGrowthChart extends React.Component {
  componentDidMount() {
    registerRenderOnWindowResize(this);
  }

  componentWillUnmount() {
    unregisterRenderOnWindowResize(this);
  }

  renderChartIntoRef(svg) {
    if (svg == null) {
      return;
    }

    const { growth, isDesktopLayout } = this.props;

    const SVG_MARGIN_OFF_Y = 7;

    let svg3 = d3.select(svg);

    const data = growth.map((d) => ({
      ...d,
      date: utcFormat(d.date)
    }));

    const tickFormat = growthTickFormat(growth);
    const tickValues = growthTickValues(growth);

    const currencyFormatting = (d) => `$${d3.format(',')(d)}`;

    const firstData = data[0];
    const lastData = data[data.length - 1];

    let minData = d3.min(data, (d) => Math.min(d.netInvestment, d.marketValue));
    let maxData = d3.max(data, (d) => Math.max(d.netInvestment, d.marketValue));

    const offset = (maxData - minData) * 0.05;
    minData = minData - offset;
    maxData = maxData + offset;

    if (minData === 0 && maxData === 0) {
      // special to show ticks
      maxData = 500;
    }

    // formula gives us 6px per digit + 25 px
    const leftMargin = Math.ceil(
      Y_TICK_PADDING +
        Math.ceil(Math.log10(maxData !== 0 ? maxData : 1)) * 6 +
        25
    );

    const margin = {
      top: 30 + SVG_MARGIN_OFF_Y,
      right: 5,
      bottom: 30,
      left: leftMargin
    };

    const svgFullWidth = svgWidth(svg);

    const plotWidth = svgFullWidth - margin.left - margin.right;
    const plotHeight = SVG_FULL_HEIGHT - margin.top - margin.bottom;

    const xAxisWidth = plotWidth;

    const plotHeightOffset = 1;

    const x = d3.scaleTime().range([0, plotWidth]);
    const y = d3.scaleLinear().range([plotHeight - plotHeightOffset, 1]);

    const xDate = (d) => x(d.date);
    const yInvestment = (d) => y(d.netInvestment);
    const yBalance = (d) => y(d.marketValue);

    x.domain(d3.extent(data, (d) => d.date));

    y.domain([minData, maxData]);

    const xAxisScale = d3
      .scaleTime()
      .range([0, xAxisWidth])
      .domain(d3.extent(data, (d) => d.date));

    const xAxis = d3
      .axisBottom(xAxisScale)
      .tickValues(tickValues)
      .tickSize(null)
      .tickFormat(d3.timeFormat(tickFormat));

    const yAxis = d3
      .axisLeft(y)
      .ticks(5)
      .tickSize(-plotWidth)
      .tickPadding(Y_TICK_PADDING)
      .tickFormat(currencyFormatting);

    const investmentLine = d3.line().x(xDate).y(yInvestment);
    const lineAbove = d3.area().curve(d3.curveLinear).x(xDate).y(yBalance);
    const lineBelow = d3.area().curve(d3.curveLinear).x(xDate).y(yBalance);

    const areaAbove = d3
      .area()
      .curve(d3.curveLinear)
      .x(xDate)
      .y0(plotHeight)
      .y1(yBalance);

    const areaBelow = d3
      .area()
      .curve(d3.curveLinear)
      .x(xDate)
      .y0(0)
      .y1(yBalance);

    const clipAbove = d3
      .area()
      .curve(d3.curveLinear)
      .x(xDate)
      .y0(0)
      .y1(yInvestment);

    const clipBelow = d3
      .area()
      .curve(d3.curveLinear)
      .x(xDate)
      .y0(plotHeight)
      .y1(yInvestment);

    // clear previous rendering
    while (svg.hasChildNodes()) {
      svg.removeChild(svg.lastChild);
    }

    svg3 = svg3
      .attr('position', 'relative')
      .attr('viewBox', `0 0 ${svgFullWidth} ${SVG_FULL_HEIGHT}`)
      .append('g')
      .datum(data);

    svg3
      .append('g')
      .attr('class', 'x axis')
      .attr(
        'transform',
        `translate(${margin.left}, ${SVG_FULL_HEIGHT - margin.bottom / 2})`
      )
      .call(xAxis);

    svg3
      .append('g')
      .attr('class', 'y axis')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
      .call(yAxis);

    const plotArea = svg3
      .append('g')
      .attr('class', 'plotarea')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    plotArea
      .append('clipPath')
      .attr('id', 'group-clip-above')
      .append('path')
      .attr('d', clipAbove);

    plotArea
      .append('clipPath')
      .attr('id', 'group-clip-below')
      .append('path')
      .attr('d', clipBelow);

    const groupAbove = plotArea
      .append('g')
      .attr('class', 'group-above')
      .attr('clip-path', 'url(#group-clip-above)');

    const groupBelow = plotArea
      .append('g')
      .attr('class', 'group-below')
      .attr('clip-path', 'url(#group-clip-below)');

    groupAbove.append('path').attr('class', 'area above').attr('d', areaAbove);
    groupBelow.append('path').attr('class', 'area below').attr('d', areaBelow);

    groupAbove.append('path').attr('class', 'line above').attr('d', lineAbove);
    groupBelow.append('path').attr('class', 'line below').attr('d', lineBelow);

    const investmentLinePath = plotArea
      .append('path')
      .attr('class', 'line')
      .attr('d', investmentLine);

    /*
     * This line component outlines a path for the dot on
     * hover to follow, it is not actually displayed on the page
     * */
    const line = d3.line().x(xDate).y(yBalance);
    const linePath = plotArea
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', 'none')
      .attr('stroke-width', 0)
      .attr('d', line);

    plotArea
      .append('circle')
      .attr('class', 'end-point')
      .attr('r', SVG_ENDPOINT_RADIUS)
      .attr('cx', xDate(firstData))
      .attr('cy', yBalance(firstData));

    plotArea
      .append('circle')
      .attr('class', 'end-point')
      .attr('r', SVG_ENDPOINT_RADIUS)
      .attr('cx', xDate(lastData))
      .attr('cy', yBalance(lastData));

    const hoverOverPointer = plotArea.append('g').style('visibility', 'hidden');

    const yHeight = SVG_FULL_HEIGHT - plotHeight;

    hoverOverPointer
      .append('line')
      .attr('x1', SVG_TOOLTIP_MID_X)
      .attr('y1', TOOLTIP_VERTICAL_PADDING)
      .attr('x2', SVG_TOOLTIP_MID_X)
      .attr('y2', plotHeight - 2 * TOOLTIP_VERTICAL_PADDING)
      .attr('class', 'tooltip-line');

    const toolTipDt = hoverOverPointer
      .append('text')
      .attr('x', SVG_TOOLTIP_MID_X)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .attr('class', 'tooltip-value');

    // Hover Legend
    const hoverLegend = hoverOverPointer.append('g');

    const rectYDefault = isDesktopLayout ? yHeight + 170 : yHeight + 125;
    const rectWidth = 210;
    const rectHeight = 100;

    const hoverLegendContainer = hoverLegend
      .append('rect')
      .attr('class', 'hover-legend-container')
      .attr('y', rectYDefault)
      .attr('width', rectWidth)
      .attr('height', rectHeight)
      .attr('rx', 4)
      .attr('fill', '#f4f5f6');

    const hoverPadding = 16;
    const hoverLegendItemSize = 10;

    // Net Investment

    hoverLegend
      .append('line')
      .attr('x1', hoverPadding)
      .attr('x2', hoverPadding + hoverLegendItemSize)
      .attr('y1', `${isDesktopLayout ? yHeight + 191.5 : yHeight + 143}`)
      .attr('y2', `${isDesktopLayout ? yHeight + 191.5 : yHeight + 143}`)
      .attr('stroke', '#3B4B59')
      .attr('stroke-width', '2');

    const legendNi = hoverLegend
      .append('text')
      .attr('x', hoverPadding * 2 + hoverLegendItemSize)
      .attr('y', isDesktopLayout ? yHeight + 195 : yHeight + 145)
      .attr('font-size', 12)
      .attr('text-anchor', 'left')
      .text('Net Investment');

    const boxNi = legendNi.node().getBBox();
    const startXNi = boxNi.x + boxNi.width + hoverPadding / 2;

    const toolTipNi = hoverLegend
      .append('text')
      .attr('x', startXNi)
      .attr('y', isDesktopLayout ? yHeight + 195 : yHeight + 145)
      .attr('text-anchor', 'left')
      .attr('class', 'tooltip-value');

    // Balance

    hoverLegend
      .append('line')
      .attr('x1', hoverPadding)
      .attr('x2', hoverPadding + hoverLegendItemSize)
      .attr('y1', `${isDesktopLayout ? yHeight + 221 : yHeight + 171}`)
      .attr('y2', `${isDesktopLayout ? yHeight + 221 : yHeight + 171}`)
      .attr('stroke', '#248647')
      .attr('stroke-width', '2');

    const legendMv = hoverLegend
      .append('text')
      .attr('x', hoverPadding * 2 + hoverLegendItemSize)
      .attr('y', isDesktopLayout ? yHeight + 225 : yHeight + 175)
      .attr('font-size', 12)
      .attr('border-bottom', '2px solid green')
      .text('Balance');

    const boxMv = legendMv.node().getBBox();
    const startXMv = boxMv.x + boxMv.width + hoverPadding / 2;

    const toolTipMv = hoverLegend
      .append('text')
      .attr('x', startXMv)
      .attr('y', isDesktopLayout ? yHeight + 225 : yHeight + 175)
      .attr('text-anchor', 'left')
      .attr('class', 'tooltip-value');

    // Balance Change

    const CvIcon = hoverLegend
      .append('circle')
      .attr('cx', hoverPadding + hoverLegendItemSize / 2)
      .attr('cy', `${isDesktopLayout ? yHeight + 250 : yHeight + 202}`)
      .attr('r', 5)
      .attr('fill', '#DD3636')
      .attr('opacity', 0.32);

    const legendCv = hoverLegend
      .append('text')
      .attr('x', hoverPadding * 2 + hoverLegendItemSize)
      .attr('y', isDesktopLayout ? yHeight + 255 : yHeight + 205)
      .attr('font-size', 12)
      .text('Balance Change');

    const boxCv = legendCv.node().getBBox();
    const startXCv = boxCv.x + boxCv.width + hoverPadding / 2;

    const toolTipCv = hoverLegend
      .append('text')
      .attr('x', startXCv)
      .attr('y', isDesktopLayout ? yHeight + 255 : yHeight + 205)
      .attr('text-anchor', 'left')
      .attr('class', 'tooltip-value');

    const mvDot = hoverOverPointer
      .append('circle')
      .attr('r', SVG_ENDPOINT_RADIUS)
      .attr('cx', SVG_TOOLTIP_MID_X)
      .attr('class', 'end-point');

    const niDot = hoverOverPointer
      .append('circle')
      .attr('r', SVG_ENDPOINT_RADIUS)
      .attr('cx', SVG_TOOLTIP_MID_X)
      .attr('class', 'end-point');

    svg3
      .append('rect')
      .attr('width', svgFullWidth)
      .attr('height', SVG_FULL_HEIGHT)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mousemove', onMouseMove)
      .on('mouseleave', () => setLegendValues(lastData));

    setLegendValues(lastData);

    function setLegendValues(inputDay) {
      const day = inputDay;
      const dt = mmmddyyyy(day.date);
      toolTipDt.attr('class', 'tooltip-value date').text(dt);

      const ni = roundUp(day.netInvestment);
      const nit = numeral(ni).format(currencyFormat);
      const nis =
        'tooltip-value ' +
        (ni > 0.0001 ? 'plus' : ni < -0.0001 ? 'minus' : 'zero');
      toolTipNi.attr('class', nis).text(nit);

      const mv = day.marketValue;
      const mvt = numeral(mv).format(currencyFormat);
      toolTipMv.attr('class', 'tooltip-value mv').text(mvt);

      const cv = mv - firstData.marketValue;
      const cvt = numeral(cv).format(currencyFormat);
      const cvs =
        'tooltip-value ' +
        (cv > 0.0001 ? 'plus' : cv < -0.0001 ? 'minus' : 'zero');
      toolTipCv.attr('class', cvs).text(cvt);
      CvIcon.attr('class', cvs).text(cvt);

      const width = getMinPossibleWidth(
        [toolTipNi.node(), toolTipMv.node(), toolTipCv.node()],
        hoverPadding
      );
      hoverLegendContainer.attr('width', width);
    }

    function onMouseMove() {
      if (
        d3.event.offsetX >= margin.left + 1 &&
        d3.event.offsetX <= plotWidth + margin.left
      ) {
        hoverOverPointer.style('visibility', 'visible');
      } else {
        hoverOverPointer.style('visibility', 'hidden');
      }

      const legendBounds = hoverLegend.node().getBBox();
      const xEdgeOfCanvas =
        d3.event.offsetX +
        legendBounds.x +
        legendBounds.width +
        TOOLTIP_HORIZONTAL_PADDING;

      // legend reflects horizontally if it is outside canvas borders
      if (svgFullWidth >= xEdgeOfCanvas && xEdgeOfCanvas >= 0) {
        hoverLegend.attr(
          'transform',
          `translate(${
            SVG_TOOLTIP_MID_X + TOOLTIP_HORIZONTAL_PADDING
          }, ${HOVER_LEGEND_VERTICAL_OFFSET})`
        );
        toolTipDt.attr('text-anchor', 'middle');
      } else {
        hoverLegend.attr(
          'transform',
          `translate(${
            -legendBounds.width + SVG_TOOLTIP_MID_X - TOOLTIP_HORIZONTAL_PADDING
          }, ${HOVER_LEGEND_VERTICAL_OFFSET})`
        );
        toolTipDt.attr('text-anchor', 'end');
      }

      const currentX = d3.event.offsetX - margin.left;

      const dv = x.invert(currentX);
      // find the index of the matching date
      let di = findIndex(data, (d) => {
        return (
          d.date.getDate() === dv.getDate() &&
          d.date.getMonth() === dv.getMonth() &&
          d.date.getFullYear() === dv.getFullYear()
        );
      });
      if (di === -1) {
        di = lastData.date.valueOf() < dv.valueOf() ? data.length - 1 : 0;
      }

      const day = data[di];

      // Calculate point on line chart to place dot
      const mvPos = getPointOnLine(currentX, linePath);

      setLegendValues(day);
      mvDot.attr('cy', mvPos.y);

      const niPos = getPointOnLine(currentX, investmentLinePath);
      niDot.attr('transform', `translate(0,${niPos.y})`);

      hoverOverPointer.attr(
        'transform',
        `translate(${d3.event.offsetX - SVG_TOOLTIP_MID_X - margin.left}, 0)`
      );
    }
  }

  render() {
    const showChart = this.props.growth.length || null;

    return (
      <div className="portfolio-growth-chart">
        {showChart && (
          <svg
            width="100%"
            height={SVG_FULL_HEIGHT}
            ref={this.renderChartIntoRef.bind(this)}
          />
        )}
        <div className="pgtchartindex">
          <span className="pgtchartindex-line investment">Net Investment</span>
          <span className="pgtchartindex-line gain">Market Gain</span>
          <span className="pgtchartindex-line loss">Market Loss</span>
        </div>
      </div>
    );
  }
}

PortfolioGrowthChart.propTypes = {
  isDesktopLayout: bool.isRequired,
  growth: array.isRequired
};

const mapStateToProps = (state) => {
  return {
    isDesktopLayout: state.view.isDesktopLayout
  };
};

export default connect(mapStateToProps)(PortfolioGrowthChart);
